import React, { useState, useEffect, useRef } from 'react';
import type { CoinData, Predictions } from '../types/interfaces';
import { loadCoinDetails, calculatePredictions, formatNumber, formatCurrency } from '../utils/coinDetails';
import { CoinDetailSkeleton } from './LoadingSkeleton';
import PriceAlertModal from './PriceAlertModal';
import CoinDescription from './CoinDescription';

interface CoinDetailModalProps {
  coinId: string;
  isOpen: boolean;
  onClose: () => void;
  cryptoData: CoinData[];
  currency?: string;
  priceAlerts?: any[];
  onCreateAlert?: (
    coinId: string,
    coinSymbol: string,
    coinName: string,
    targetPrice: number,
    direction: 'above' | 'below',
    currency: string,
    emailNotification: boolean
  ) => Promise<any>;
  onDeleteAlert?: (alertId: string) => void;
}

declare global {
  interface Window {
    Chart: any;
    bsvPayments?: {
      requestPayment: (action: string, amount: number) => Promise<any>;
      getPaymentAmount: (action: string) => number;
      hasPaidForAction: (action: string) => boolean;
    };
  }
}

export default function CoinDetailModal({ 
  coinId, 
  isOpen, 
  onClose, 
  cryptoData,
  currency = 'usd',
  priceAlerts = [],
  onCreateAlert,
  onDeleteAlert
}: CoinDetailModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coin, setCoin] = useState<CoinData | null>(null);
  const [predictions, setPredictions] = useState<Predictions | null>(null);
  const [description, setDescription] = useState<string>('');
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [chartValues, setChartValues] = useState<number[]>([]);
  const [chartTimeframe, setChartTimeframe] = useState<number | 'max'>(30); // Chart timeframe in days or 'max'
  const [selectedTimeframe, setSelectedTimeframe] = useState<'7d' | '30d' | '90d'>('7d');
  const [showPriceAlertModal, setShowPriceAlertModal] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen && coinId) {
      const loadDetails = async () => {
        try {
          setLoading(true);
          setError(null);

          // Find coin in cryptoData
          const foundCoin = cryptoData.find(c => c.id === coinId);
          if (!foundCoin) {
            setError('Coin not found');
            setLoading(false);
            return;
          }

          // ALWAYS request payment (250 satoshis) every time - no checking hasPaidForAction
          console.log('CoinDetailModal: Starting payment request for VIEW_DETAILS');
          console.log('CoinDetailModal: bsvPayments available?', !!window.bsvPayments);
          
            if (!window.bsvPayments) {
              console.error('CoinDetailModal: window.bsvPayments is not available');
              setError('Connect MetaNet wallet');
              setLoading(false);
              return;
            }

          if (typeof window.bsvPayments.getPaymentAmount !== 'function' || typeof window.bsvPayments.requestPayment !== 'function') {
            console.error('CoinDetailModal: bsvPayments methods not available');
            setError('Connect MetaNet wallet');
            setLoading(false);
            return;
          }

          try {
            const amount = window.bsvPayments.getPaymentAmount('VIEW_DETAILS');
            console.log('CoinDetailModal: Requesting payment of', amount, 'satoshis for VIEW_DETAILS');
            
            // Always request payment - no hasPaidForAction check (user wants payment every time)
            const paymentResult = await window.bsvPayments.requestPayment('VIEW_DETAILS', amount);
            console.log('CoinDetailModal: Payment result:', paymentResult);
            
            // If payment returns null, it was cancelled, failed, or wallet not available
            if (!paymentResult) {
              console.log('CoinDetailModal: Payment cancelled, failed, or wallet not available');
              setError('Connect MetaNet wallet');
              setLoading(false);
              return;
            }
            
            console.log('CoinDetailModal: Payment successful, proceeding to load details');
          } catch (paymentError) {
            console.error('CoinDetailModal: Payment error:', paymentError);
            setError('Connect MetaNet wallet');
            setLoading(false);
            return;
          }

          console.log('Payment successful, loading coin details');

          // Load coin details with initial timeframe
          const timeframeParam = chartTimeframe === 'max' ? 'max' : (typeof chartTimeframe === 'number' ? chartTimeframe : 30);
          const details = await loadCoinDetails(coinId, foundCoin, timeframeParam);
          setCoin(details.coin);
          setPredictions(details.predictions);
          setChartLabels(details.labels);
          setChartValues(details.values);
          setDescription(details.description || '');

          setLoading(false);
        } catch (err) {
          console.error('Error loading coin details:', err);
          setError(err instanceof Error ? err.message : 'Failed to load coin details');
          setLoading(false);
        }
      };

      loadDetails();
    } else {
      // Reset state when modal closes
      setLoading(true);
      setError(null);
      setCoin(null);
      setPredictions(null);
      setChartLabels([]);
      setChartValues([]);
      setDescription('');
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    }
  }, [isOpen, coinId, cryptoData, onClose]);

  // Render chart when data is ready
  useEffect(() => {
    if (!loading && !error && chartLabels.length > 0 && chartValues.length > 0 && chartRef.current) {
      if (!window.Chart) {
        console.error('Chart.js is not loaded!');
        return;
      }
      // Destroy existing chart
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      // Create new chart
      const ctx = chartRef.current.getContext('2d');
      chartInstanceRef.current = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: chartLabels,
          datasets: [
            {
              label: 'Price (USD)',
              data: chartValues,
              borderColor: 'rgb(99, 102, 241)',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              labels: {
                color: '#cbd5e1'
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(30, 41, 59, 0.95)',
              titleColor: '#f1f5f9',
              bodyColor: '#f1f5f9',
              borderColor: '#6366f1',
              borderWidth: 1,
              callbacks: {
                label: function(context: any) {
                  return 'Price: $' + formatNumber(context.parsed.y);
                }
              }
            }
          },
          scales: {
            x: {
              ticks: {
                color: '#cbd5e1',
                maxTicksLimit: 10
              },
              grid: {
                color: 'rgba(71, 85, 105, 0.3)'
              }
            },
            y: {
              ticks: {
                color: '#cbd5e1',
                callback: function(value: any) {
                  return '$' + formatNumber(value);
                }
              },
              grid: {
                color: 'rgba(71, 85, 105, 0.3)'
              }
            }
          }
        }
      });
    }

    // Cleanup chart on unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [loading, error, chartLabels, chartValues]);

  // Load chart data when timeframe changes
  useEffect(() => {
    if (coin && coinId && !loading && isOpen) {
      const loadChartData = async () => {
        setLoadingChart(true);
        try {
          const foundCoin = cryptoData.find(c => c.id === coinId);
          if (!foundCoin) return;
          
          const timeframeParam = chartTimeframe === 'max' ? 'max' : (typeof chartTimeframe === 'number' ? chartTimeframe : 30);
          const details = await loadCoinDetails(coinId, foundCoin, timeframeParam);
          setChartLabels(details.labels);
          setChartValues(details.values);
        } catch (err) {
          console.error('Error loading chart data:', err);
        } finally {
          setLoadingChart(false);
        }
      };
      
      loadChartData();
    }
  }, [chartTimeframe, coinId, isOpen]);

  if (!isOpen) return null;

  const priceChange = coin?.price_change_percentage_24h || 0;
  const currentPrediction = predictions
    ? selectedTimeframe === '7d'
      ? { price: predictions.day7, change: predictions.day7Change, label: '7 Day' }
      : selectedTimeframe === '30d'
      ? { price: predictions.day30, change: predictions.day30Change, label: '30 Day' }
      : { price: predictions.day90, change: predictions.day90Change, label: '90 Day' }
    : null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-[900px] w-[95%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
        >
          &times;
        </button>

        {loading && <CoinDetailSkeleton />}

        {error && (
          <div className="text-center py-14 px-5 text-danger text-lg">Error: {error}</div>
        )}

        {!loading && !error && coin && (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-6 mb-8 bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-3xl border-2 border-primary/10 shadow-bubble">
                {coin.image && (
                  <div className="relative">
                    <img
                      src={coin.image}
                      alt={coin.name}
                      className="w-20 h-20 rounded-2xl object-cover ring-4 ring-primary/20 shadow-lg"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22%3E%3Crect fill=%22%236366f1%22 width=%2280%22 height=%2280%22 rx=%2216%22/%3E%3C/svg%3E';
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-primary to-primary-light rounded-full border-4 border-bg-secondary shadow-lg"></div>
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">
                    {coin.name} ({coin.symbol.toUpperCase()})
                  </h2>
                  <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">{formatNumber(coin.current_price)}</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className={`text-lg font-bold px-5 py-2 rounded-full inline-block ${
                      priceChange >= 0 
                        ? 'text-green-400 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 shadow-glow-green' 
                        : 'text-red-400 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30'
                    }`}>
                      {priceChange >= 0 ? '↗' : '↘'} {priceChange >= 0 ? '+' : ''}
                      {priceChange.toFixed(2)}% (24h)
                    </div>
                    {onCreateAlert && (
                      <button
                        onClick={() => setShowPriceAlertModal(true)}
                        className="px-5 py-2 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95 flex items-center gap-2"
                        title="Set Price Alert (100 sats)"
                      >
                        🔔 Alert
                      </button>
                    )}
                  </div>
                  {priceAlerts && priceAlerts.length > 0 && (
                    <div className="mt-3 text-xs text-text-secondary">
                      {priceAlerts.length} active alert{priceAlerts.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-bg to-bg-secondary p-5 rounded-2xl border-2 border-primary/10 shadow-bubble">
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Market Cap</div>
                  <div className="text-lg font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(coin.market_cap)}</div>
                </div>
                <div className="bg-gradient-to-br from-bg to-bg-secondary p-5 rounded-2xl border-2 border-primary/10 shadow-bubble">
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">24h Volume</div>
                  <div className="text-lg font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(coin.total_volume)}</div>
                </div>
                <div className="bg-gradient-to-br from-bg to-bg-secondary p-5 rounded-2xl border-2 border-primary/10 shadow-bubble">
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">24h High</div>
                  <div className="text-lg font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(coin.high_24h)}</div>
                </div>
                <div className="bg-gradient-to-br from-bg to-bg-secondary p-5 rounded-2xl border-2 border-primary/10 shadow-bubble">
                  <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">24h Low</div>
                  <div className="text-lg font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatCurrency(coin.low_24h)}</div>
                </div>
              </div>
            </div>

            {/* Chart Timeframe Selector */}
            <div className="mb-4">
              <div className="flex gap-2 flex-wrap p-2 bg-bg rounded-full w-fit">
                {[
                  { label: '24H', days: 1 },
                  { label: '7D', days: 7 },
                  { label: '30D', days: 30 },
                  { label: '90D', days: 90 },
                  { label: '180D', days: 180 },
                  { label: '1Y', days: 365 },
                  { label: 'MAX', days: 'max' as const }
                ].map(({ label, days }) => {
                  const isSelected = days === 'max' 
                    ? chartTimeframe === 'max' || chartTimeframe === 9999
                    : chartTimeframe === days;
                  const isCurrentlyLoading = loadingChart && isSelected;
                  
                  return (
                    <button
                      key={label}
                      onClick={() => setChartTimeframe(days === 'max' ? 'max' : days)}
                      disabled={loadingChart}
                      className={`px-4 py-2 rounded-full border-2 transition-all duration-300 font-semibold text-sm ${
                        isSelected
                          ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white shadow-bubble-lg scale-105'
                          : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isCurrentlyLoading ? '...' : label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-6" style={{ height: '300px', position: 'relative' }}>
              {loadingChart && (
                <div className="absolute inset-0 flex items-center justify-center bg-bg/50 rounded-lg z-10">
                  <div className="text-text-secondary">Loading chart data...</div>
                </div>
              )}
              <canvas ref={chartRef}></canvas>
            </div>

            {predictions && (
              <div className="border-t border-border pt-6">
                <h3 className="text-xl font-bold mb-4">📈 Price Prediction</h3>
                <div className="flex gap-3 mb-6 p-2 bg-bg rounded-full w-fit">
                  <button
                    onClick={() => setSelectedTimeframe('7d')}
                    className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                      selectedTimeframe === '7d'
                        ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white shadow-bubble-lg scale-105'
                        : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
                    }`}
                  >
                    7 Days
                  </button>
                  <button
                    onClick={() => setSelectedTimeframe('30d')}
                    className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                      selectedTimeframe === '30d'
                        ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white shadow-bubble-lg scale-105'
                        : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
                    }`}
                  >
                    30 Days
                  </button>
                  <button
                    onClick={() => setSelectedTimeframe('90d')}
                    className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                      selectedTimeframe === '90d'
                        ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white shadow-bubble-lg scale-105'
                        : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
                    }`}
                  >
                    90 Days
                  </button>
                </div>
                {currentPrediction && (
                  <div className="bg-gradient-to-br from-bg to-bg-secondary p-8 rounded-3xl mb-6 border-2 border-primary/20 shadow-bubble">
                    <h4 className="text-xl font-bold mb-4 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">{currentPrediction.label} Prediction</h4>
                    <div className="text-4xl font-bold mb-3 bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">{formatNumber(currentPrediction.price)}</div>
                    <div
                      className={`text-lg font-bold px-5 py-2 rounded-full inline-block ${
                        currentPrediction.change >= 0 
                          ? 'text-green-400 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 shadow-glow-green' 
                          : 'text-red-400 bg-gradient-to-r from-red-500/20 to-red-600/20 border border-red-500/30'
                      }`}
                    >
                      {currentPrediction.change >= 0 ? '↗' : '↘'} {currentPrediction.change >= 0 ? '+' : ''}
                      {currentPrediction.change.toFixed(2)}% change
                    </div>
                    <p className="text-text-secondary text-sm mt-4 font-medium">
                      📊 Based on 30-day trend analysis
                    </p>
                  </div>
                )}
                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/50 rounded-3xl p-6 text-yellow-400 text-sm shadow-bubble">
                  <strong>⚠️ Disclaimer</strong>
                  <p className="mt-2">
                    Price predictions are generated using historical trend analysis and should NOT be considered financial advice.
                    Cryptocurrency markets are highly volatile and unpredictable. Past performance does not guarantee future results.
                    Always do your own research and consult with financial advisors before making investment decisions.
                  </p>
                </div>
              </div>
            )}

            {/* Coin Description Section */}
            {coin && (
              <div className="mt-6">
                <CoinDescription
                  coinName={coin.name}
                  coinSymbol={coin.symbol}
                  description={description}
                  loading={loading}
                />
              </div>
            )}
          </>
        )}

        {coin && onCreateAlert && (
          <PriceAlertModal
            isOpen={showPriceAlertModal}
            onClose={() => setShowPriceAlertModal(false)}
            coin={coin}
            onCreateAlert={onCreateAlert}
            existingAlerts={priceAlerts}
            currency={currency}
          />
        )}
      </div>
    </div>
  );
}
